import Layout from "@/components/Layout";
import PermissionDenied from "@/components/PermissionDenied";
import { ConfirmAlertDialog } from "@/components/ConfirmAlertDialog";
import { GridPaginationFooter, GridPerPageSelect } from "@/components/catalog/GridPaginationControls";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { listEmployees, syncEmployeesFromApi, type EmployeeRecord } from "@/lib/hr/employeesStore";
import { listSalaries } from "@/lib/hr/salaryProcessStore";
import {
  createSalaryPayment,
  deleteSalaryPayment,
  listSalaryPayments,
  markSalaryPaymentPaid,
  previewSalaryPaymentFromSalary,
  salaryPaymentsApiErrorMessage,
  updateSalaryPayment,
  type SalaryPaymentRecord,
  type SalaryPaymentStatus,
} from "@/lib/hr/salaryPaymentsStore";
import { can, getStoredAuthUser } from "@/lib/authUser";
import { cn } from "@/lib/utils";
import { isAxiosError } from "axios";
import { Banknote, Check, ChevronsUpDown, Coins, Filter, LayoutGrid, List, Loader2, Plus, Search, User, Wallet } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

const PAGE_SIZE_OPTIONS = [12, 20, 32, 48] as const;

const MONTHS = [
  { v: 1, label: "January" },
  { v: 2, label: "February" },
  { v: 3, label: "March" },
  { v: 4, label: "April" },
  { v: 5, label: "May" },
  { v: 6, label: "June" },
  { v: 7, label: "July" },
  { v: 8, label: "August" },
  { v: 9, label: "September" },
  { v: 10, label: "October" },
  { v: 11, label: "November" },
  { v: 12, label: "December" },
];

function formatMoney(s: string): string {
  const n = Number(s);
  if (!Number.isFinite(n)) return s;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function employeeDisplayName(e: EmployeeRecord): string {
  return (e.fullName || `${e.firstName} ${e.lastName}`).trim() || e.id;
}

function employeePickerLabel(e: EmployeeRecord): string {
  const n = employeeDisplayName(e);
  return e.employeeCode ? `${e.employeeCode} · ${n}` : n;
}

function employeeInitials(e: EmployeeRecord): string {
  const first = (e.firstName || "").trim();
  const last = (e.lastName || "").trim();
  if (first && last) return `${first[0]}${last[0]}`.toUpperCase();
  const full = (e.fullName || "").trim();
  if (full) {
    const parts = full.split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : full.slice(0, 2).toUpperCase();
  }
  return "??";
}

function employeeSubtitle(e: EmployeeRecord): string {
  const parts: string[] = [];
  if (e.departmentName?.trim()) parts.push(e.departmentName.trim());
  if (e.designationName?.trim()) parts.push(e.designationName.trim());
  return parts.join(" · ") || "";
}

function isActiveEmployee(e: EmployeeRecord): boolean {
  return (e.employeeStatus || "").toLowerCase() === "active";
}

function statusPill(status: SalaryPaymentStatus) {
  if (status === "paid") {
    return (
      <span className="inline-flex rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 dark:text-emerald-200">
        Paid
      </span>
    );
  }
  return (
    <span className="inline-flex rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-semibold text-amber-900 dark:text-amber-100">
      Pending
    </span>
  );
}

export default function SalaryPayments() {
  const [user, setUser] = useState(() => getStoredAuthUser());
  const canCreate = can(user, "salary_payment_can_create");
  const canUpdate = can(user, "salary_payment_can_update");
  const canDelete = can(user, "salary_payment_can_delete");
  const [isForbidden, setIsForbidden] = useState(false);

  const [empTick, setEmpTick] = useState(0);
  const employees = useMemo(() => listEmployees(), [empTick]);
  const activeEmployees = useMemo(() => employees.filter(isActiveEmployee), [employees]);

  const [rows, setRows] = useState<SalaryPaymentRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<(typeof PAGE_SIZE_OPTIONS)[number]>(PAGE_SIZE_OPTIONS[1]);
  const [meta, setMeta] = useState({ lastPage: 1, total: 0, perPage: 20 });

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterYear, setFilterYear] = useState(() => new Date().getFullYear());
  const [filterMonth, setFilterMonth] = useState<number | "__all__">("__all__");
  const [filterStatus, setFilterStatus] = useState<SalaryPaymentStatus | "__all__">("__all__");
  const [filterEmployeeId, setFilterEmployeeId] = useState<string>("__all__");
  const [filterEmployeePickerOpen, setFilterEmployeePickerOpen] = useState(false);
  const [filterEmployeePickerCycle, setFilterEmployeePickerCycle] = useState(0);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");

  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draftEmployee, setDraftEmployee] = useState("");
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerCycle, setEmployeePickerCycle] = useState(0);
  const [draftYear, setDraftYear] = useState(() => new Date().getFullYear());
  const [draftMonth, setDraftMonth] = useState(() => new Date().getMonth() + 1);
  const [draftSalaryId, setDraftSalaryId] = useState<string | null>(null);
  const [draftBasic, setDraftBasic] = useState("");
  const [draftBonus, setDraftBonus] = useState("");
  const [draftDeductions, setDraftDeductions] = useState("");
  const [draftNet, setDraftNet] = useState("");
  const [draftNotes, setDraftNotes] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<SalaryPaymentRecord | null>(null);
  const [editBasic, setEditBasic] = useState("");
  const [editBonus, setEditBonus] = useState("");
  const [editDeductions, setEditDeductions] = useState("");
  const [editNet, setEditNet] = useState("");
  const [editNotes, setEditNotes] = useState("");

  const [deleteTarget, setDeleteTarget] = useState<SalaryPaymentRecord | null>(null);
  const [markTarget, setMarkTarget] = useState<SalaryPaymentRecord | null>(null);
  const [markDate, setMarkDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    void syncEmployeesFromApi()
      .catch(() => {})
      .finally(() => setEmpTick((t) => t + 1));
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query.trim()), 450);
    return () => window.clearTimeout(t);
  }, [query]);

  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2, y - 3];
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setIsForbidden(false);
    try {
      const res = await listSalaryPayments({
        page,
        perPage: pageSize,
        periodYear: filterYear,
        periodMonth: filterMonth === "__all__" ? undefined : filterMonth,
        status: filterStatus,
        employeeId: filterEmployeeId === "__all__" ? undefined : filterEmployeeId,
        search: debouncedQuery || undefined,
      });
      setRows(res.rows);
      setMeta({ lastPage: res.meta.lastPage, total: res.meta.total, perPage: res.meta.perPage });
    } catch (e) {
      if (isAxiosError(e) && e.response?.status === 403) {
        setIsForbidden(true);
        setRows([]);
        return;
      }
      toast.error(salaryPaymentsApiErrorMessage(e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterYear, filterMonth, filterStatus, filterEmployeeId, debouncedQuery]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setPage(1);
  }, [filterYear, filterMonth, filterStatus, filterEmployeeId, pageSize, debouncedQuery]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === "auth_user") setUser(getStoredAuthUser());
    };
    const onAuth = () => setUser(getStoredAuthUser());
    window.addEventListener("storage", onStorage);
    window.addEventListener("auth_user_updated", onAuth);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("auth_user_updated", onAuth);
    };
  }, []);

  const selectedDraftEmployee = useMemo(
    () => (draftEmployee ? activeEmployees.find((e) => e.id === draftEmployee) ?? employees.find((e) => e.id === draftEmployee) : undefined),
    [draftEmployee, activeEmployees, employees],
  );

  const selectedFilterEmployee = useMemo(
    () =>
      filterEmployeeId !== "__all__"
        ? activeEmployees.find((e) => e.id === filterEmployeeId) ?? employees.find((e) => e.id === filterEmployeeId)
        : undefined,
    [filterEmployeeId, activeEmployees, employees],
  );

  const openCreate = () => {
    setDraftEmployee(activeEmployees[0]?.id ?? "");
    setDraftYear(new Date().getFullYear());
    setDraftMonth(new Date().getMonth() + 1);
    setDraftSalaryId(null);
    setDraftBasic("");
    setDraftBonus("");
    setDraftDeductions("");
    setDraftNet("");
    setDraftNotes("");
    setEmployeePickerOpen(false);
    setCreateOpen(true);
  };

  const loadFromPayroll = async () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create salary payments.");
      return;
    }
    if (!draftEmployee) {
      toast.error("Select an employee.");
      return;
    }
    setLoadingPreview(true);
    try {
      const { rows } = await listSalaries({
        employeeId: draftEmployee,
        periodYear: draftYear,
        periodMonth: draftMonth,
        perPage: 5,
        page: 1,
      });
      const first = rows[0];
      if (!first) {
        toast.message("No payroll salary found for this employee and period. Enter amounts manually.");
        setDraftSalaryId(null);
        return;
      }
      setDraftSalaryId(first.id);
      const snap = await previewSalaryPaymentFromSalary(first.id);
      setDraftBasic(String(snap.basicSalary));
      setDraftBonus(String(snap.bonus));
      setDraftDeductions(String(snap.deductions));
      setDraftNet(String(snap.netSalary));
      toast.success("Loaded figures from payroll.");
    } catch (e) {
      toast.error(salaryPaymentsApiErrorMessage(e));
    } finally {
      setLoadingPreview(false);
    }
  };

  const submitCreate = async () => {
    if (!canCreate) {
      toast.error("You don’t have permission to create salary payments.");
      return;
    }
    if (!draftEmployee) {
      toast.error("Select an employee.");
      return;
    }
    setSaving(true);
    try {
      await createSalaryPayment({
        employeeId: draftEmployee,
        periodYear: draftYear,
        periodMonth: draftMonth,
        salaryId: draftSalaryId,
        basicSalary: draftBasic,
        bonus: draftBonus,
        deductions: draftDeductions,
        netSalary: draftNet,
        notes: draftNotes || null,
      });
      toast.success("Salary payment recorded.");
      setCreateOpen(false);
      await load();
    } catch (e) {
      toast.error(salaryPaymentsApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (r: SalaryPaymentRecord) => {
    setEditing(r);
    setEditBasic(r.basicSalary);
    setEditBonus(r.bonus);
    setEditDeductions(r.deductions);
    setEditNet(r.netSalary);
    setEditNotes(r.notes ?? "");
    setEditOpen(true);
  };

  const submitEdit = async () => {
    if (!editing) return;
    if (!canUpdate) {
      toast.error("You don’t have permission to update salary payments.");
      return;
    }
    setSaving(true);
    try {
      await updateSalaryPayment(editing.id, {
        basicSalary: editBasic,
        bonus: editBonus,
        deductions: editDeductions,
        netSalary: editNet,
        notes: editNotes || null,
      });
      toast.success("Updated.");
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(salaryPaymentsApiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const confirmMarkPaid = async () => {
    if (!markTarget) return;
    if (!canUpdate) {
      toast.error("You don’t have permission to update salary payments.");
      return;
    }
    setActionLoading(true);
    try {
      await markSalaryPaymentPaid(markTarget.id, markDate);
      toast.success("Marked as paid.");
      setMarkTarget(null);
      await load();
    } catch (e) {
      toast.error(salaryPaymentsApiErrorMessage(e));
    } finally {
      setActionLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (!canDelete) {
      toast.error("You don’t have permission to delete salary payments.");
      return;
    }
    setActionLoading(true);
    try {
      await deleteSalaryPayment(deleteTarget.id);
      toast.success("Deleted.");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(salaryPaymentsApiErrorMessage(e));
    } finally {
      setActionLoading(false);
    }
  };

  const rangeStart = meta.total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, meta.total);

  if (isForbidden) {
    return (
      <PermissionDenied
        title="Salary payments access required"
        message="You don’t have permission to view salary payments. Ask an administrator to grant salary_payment_can_view_any."
      />
    );
  }

  return (
    <Layout title="Salary payments">
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-display font-bold text-foreground">Salary payments</h1>
            <p className="text-sm text-muted-foreground mt-1">Record and track net salary payouts by period.</p>
          </div>
          {canCreate ? (
            <Button variant="premium" className="gap-2 shrink-0" onClick={openCreate} disabled={activeEmployees.length === 0}>
              <Plus className="w-4 h-4" />
              New payment
            </Button>
          ) : null}
        </div>

        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>New salary payment</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label>Employee</Label>
                <Popover
                  modal
                  open={employeePickerOpen}
                  onOpenChange={(next) => {
                    setEmployeePickerOpen(next);
                    if (next) setEmployeePickerCycle((c) => c + 1);
                  }}
                >
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={employeePickerOpen}
                      className={cn(
                        "glass-input h-10 w-full justify-between px-3 font-normal",
                        !draftEmployee && "text-muted-foreground",
                      )}
                    >
                      {selectedDraftEmployee ? (
                        <span className="flex items-center gap-2 truncate">
                          <Avatar className="h-5 w-5 text-[8px]">
                            {selectedDraftEmployee.photoDataUrl ? (
                              <AvatarImage
                                src={selectedDraftEmployee.photoDataUrl}
                                alt={employeeDisplayName(selectedDraftEmployee)}
                              />
                            ) : null}
                            <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                              {employeeInitials(selectedDraftEmployee)}
                            </AvatarFallback>
                          </Avatar>
                          <span className="truncate">{employeePickerLabel(selectedDraftEmployee)}</span>
                        </span>
                      ) : (
                        <span className="truncate text-left">Select employee…</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                    <Command key={employeePickerCycle} shouldFilter>
                      <CommandInput placeholder="Search name, code or department…" className="h-9" />
                      <CommandList>
                        <CommandEmpty>No employee found.</CommandEmpty>
                        <CommandGroup>
                          {activeEmployees.map((e) => {
                            const label = employeePickerLabel(e);
                            const subtitle = employeeSubtitle(e);
                            const selected = draftEmployee === e.id;
                            const searchValue = [label, e.employeeCode, e.departmentName, e.designationName, e.id]
                              .filter(Boolean)
                              .join(" ");
                            return (
                              <CommandItem
                                key={e.id}
                                value={searchValue}
                                onSelect={() => {
                                  setDraftEmployee(e.id);
                                  setEmployeePickerOpen(false);
                                }}
                                className="gap-2"
                              >
                                <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                                <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                                  {e.photoDataUrl ? (
                                    <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                                  ) : null}
                                  <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                    {employeeInitials(e)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="flex flex-col min-w-0">
                                  <span className="truncate text-sm font-medium">{label}</span>
                                  {subtitle ? (
                                    <span className="truncate text-[11px] opacity-60 leading-tight">{subtitle}</span>
                                  ) : null}
                                </div>
                              </CommandItem>
                            );
                          })}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select value={String(draftYear)} onValueChange={(v) => setDraftYear(Number(v))}>
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select value={String(draftMonth)} onValueChange={(v) => setDraftMonth(Number(v))}>
                    <SelectTrigger className="glass-input h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MONTHS.map((m) => (
                        <SelectItem key={m.v} value={String(m.v)}>
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {canCreate ? (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => void loadFromPayroll()}
                  disabled={loadingPreview}
                >
                  {loadingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Banknote className="w-4 h-4" />}
                  Load amounts from payroll
                </Button>
              ) : null}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Basic salary</Label>
                  <Input className="glass-input h-10" value={draftBasic} onChange={(e) => setDraftBasic(e.target.value)} inputMode="decimal" />
                </div>
                <div className="space-y-2">
                  <Label>Bonus</Label>
                  <Input className="glass-input h-10" value={draftBonus} onChange={(e) => setDraftBonus(e.target.value)} inputMode="decimal" />
                </div>
                <div className="space-y-2">
                  <Label>Deductions</Label>
                  <Input
                    className="glass-input h-10"
                    value={draftDeductions}
                    onChange={(e) => setDraftDeductions(e.target.value)}
                    inputMode="decimal"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Net salary</Label>
                  <Input className="glass-input h-10" value={draftNet} onChange={(e) => setDraftNet(e.target.value)} inputMode="decimal" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes (optional)</Label>
                <Input className="glass-input h-10" value={draftNotes} onChange={(e) => setDraftNotes(e.target.value)} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setCreateOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void submitCreate()} disabled={saving || !canCreate}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Edit payment</DialogTitle>
            </DialogHeader>
            {editing?.status === "paid" ? (
              <div className="space-y-3 py-2">
                <p className="text-sm text-muted-foreground">Only notes can be edited for paid records.</p>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Input className="glass-input h-10" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 py-2">
                <div className="space-y-2">
                  <Label>Basic salary</Label>
                  <Input className="glass-input h-10" value={editBasic} onChange={(e) => setEditBasic(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Bonus</Label>
                  <Input className="glass-input h-10" value={editBonus} onChange={(e) => setEditBonus(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Deductions</Label>
                  <Input className="glass-input h-10" value={editDeductions} onChange={(e) => setEditDeductions(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Net salary</Label>
                  <Input className="glass-input h-10" value={editNet} onChange={(e) => setEditNet(e.target.value)} />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label>Notes</Label>
                  <Input className="glass-input h-10" value={editNotes} onChange={(e) => setEditNotes(e.target.value)} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="glass" onClick={() => setEditOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void submitEdit()} disabled={saving || !canUpdate}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="glass-card p-4 rounded-xl border border-border/50 space-y-3">
          <div className="flex flex-col lg:flex-row gap-3 lg:items-center">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by employee name or code…"
                className="glass-input pl-9 h-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void load()}
              />
            </div>
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="glass" className="gap-2 shrink-0">
                  <Filter className="w-4 h-4" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[92vw] sm:w-[400px]">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                  <SheetDescription>Refine payments by period and status.</SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-4">
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={String(filterYear)} onValueChange={(v) => setFilterYear(Number(v))}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {yearOptions.map((y) => (
                          <SelectItem key={y} value={String(y)}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={filterMonth === "__all__" ? "__all__" : String(filterMonth)} onValueChange={(v) => setFilterMonth(v === "__all__" ? "__all__" : Number(v))}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All months</SelectItem>
                        {MONTHS.map((m) => (
                          <SelectItem key={m.v} value={String(m.v)}>
                            {m.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as SalaryPaymentStatus | "__all__")}>
                      <SelectTrigger className="glass-input h-10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__all__">All</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Employee</Label>
                    <Popover
                      modal
                      open={filterEmployeePickerOpen}
                      onOpenChange={(next) => {
                        setFilterEmployeePickerOpen(next);
                        if (next) setFilterEmployeePickerCycle((c) => c + 1);
                      }}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          role="combobox"
                          aria-expanded={filterEmployeePickerOpen}
                          className={cn(
                            "glass-input h-10 w-full justify-between px-3 font-normal",
                            filterEmployeeId === "__all__" && "text-muted-foreground",
                          )}
                        >
                          {selectedFilterEmployee ? (
                            <span className="flex items-center gap-2 truncate">
                              <Avatar className="h-5 w-5 text-[8px]">
                                {selectedFilterEmployee.photoDataUrl ? (
                                  <AvatarImage
                                    src={selectedFilterEmployee.photoDataUrl}
                                    alt={employeeDisplayName(selectedFilterEmployee)}
                                  />
                                ) : null}
                                <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                                  {employeeInitials(selectedFilterEmployee)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="truncate">{employeePickerLabel(selectedFilterEmployee)}</span>
                            </span>
                          ) : (
                            <span className="truncate text-left">All employees</span>
                          )}
                          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" sideOffset={4}>
                        <Command key={filterEmployeePickerCycle} shouldFilter>
                          <CommandInput placeholder="Search name, code or department…" className="h-9" />
                          <CommandList>
                            <CommandEmpty>No employee found.</CommandEmpty>
                            <CommandGroup>
                              <CommandItem
                                value="__all__ all employees"
                                onSelect={() => {
                                  setFilterEmployeeId("__all__");
                                  setFilterEmployeePickerOpen(false);
                                }}
                                className="gap-2"
                              >
                                <Check
                                  className={cn(
                                    "h-3.5 w-3.5 shrink-0",
                                    filterEmployeeId === "__all__" ? "opacity-100" : "opacity-0",
                                  )}
                                />
                                <User className="h-4 w-4 text-muted-foreground" />
                                <span>All employees</span>
                              </CommandItem>
                              {activeEmployees.map((e) => {
                                const label = employeePickerLabel(e);
                                const subtitle = employeeSubtitle(e);
                                const selected = filterEmployeeId === e.id;
                                const searchValue = [label, e.employeeCode, e.departmentName, e.designationName, e.id]
                                  .filter(Boolean)
                                  .join(" ");
                                return (
                                  <CommandItem
                                    key={e.id}
                                    value={searchValue}
                                    onSelect={() => {
                                      setFilterEmployeeId(e.id);
                                      setFilterEmployeePickerOpen(false);
                                    }}
                                    className="gap-2"
                                  >
                                    <Check className={cn("h-3.5 w-3.5 shrink-0", selected ? "opacity-100" : "opacity-0")} />
                                    <Avatar className="h-7 w-7 shrink-0 text-[10px]">
                                      {e.photoDataUrl ? (
                                        <AvatarImage src={e.photoDataUrl} alt={employeeDisplayName(e)} />
                                      ) : null}
                                      <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                                        {employeeInitials(e)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex flex-col min-w-0">
                                      <span className="truncate text-sm font-medium">{label}</span>
                                      {subtitle ? (
                                        <span className="truncate text-[11px] opacity-60 leading-tight">{subtitle}</span>
                                      ) : null}
                                    </div>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
                <SheetFooter className="mt-6">
                  <Button type="button" variant="glass" className="w-full" onClick={() => void load()}>
                    Apply
                  </Button>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </div>

          <div className="flex flex-wrap items-center gap-3 justify-between">
            <GridPerPageSelect id="salary-payments-page-size" pageSize={pageSize} onPageSizeChange={(n) => setPageSize(n as (typeof PAGE_SIZE_OPTIONS)[number])} options={PAGE_SIZE_OPTIONS} />
            <div className="inline-flex h-10 overflow-hidden rounded-lg border border-border bg-background shrink-0 ml-auto">
              <Button
                type="button"
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="sm"
                className="rounded-none px-3"
                onClick={() => setViewMode("grid")}
                aria-label="Grid view"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="sm"
                className="rounded-none px-3"
                onClick={() => setViewMode("list")}
                aria-label="List view"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20 text-muted-foreground">
            <Loader2 className="h-10 w-10 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="glass-card rounded-xl border border-border/50 p-12 text-center text-sm text-muted-foreground">No salary payments match your filters.</div>
        ) : viewMode === "grid" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {rows.map((r) => (
              <div
                key={r.id}
                className="rounded-xl border border-border/60 bg-card/80 shadow-sm overflow-hidden flex flex-col"
              >
                <div className="h-1 w-full bg-primary/80" />
                <div className="p-4 space-y-3 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-foreground truncate">{r.employeeName}</div>
                      <div className="text-xs text-muted-foreground font-mono mt-0.5">
                        {r.periodLabel}
                        {r.employeeCode ? ` · ${r.employeeCode}` : ""}
                      </div>
                    </div>
                    {statusPill(r.status)}
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
                      <div className="text-muted-foreground">Net salary</div>
                      <div className="font-semibold tabular-nums text-emerald-700 dark:text-emerald-300">{formatMoney(r.netSalary)}</div>
                    </div>
                    <div className="rounded-lg border border-border/50 bg-muted/20 p-2">
                      <div className="text-muted-foreground">Deductions</div>
                      <div className="font-semibold tabular-nums">{formatMoney(r.deductions)}</div>
                    </div>
                  </div>
                  <div className="text-[11px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                    <span>
                      Basic <span className="text-foreground font-medium tabular-nums">{formatMoney(r.basicSalary)}</span>
                    </span>
                    <span>
                      Bonus <span className="text-foreground font-medium tabular-nums">{formatMoney(r.bonus)}</span>
                    </span>
                  </div>
                  {r.paymentDate ? (
                    <div className="text-[11px] text-muted-foreground">
                      Paid on <span className="text-foreground">{r.paymentDate}</span>
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap gap-2 p-3 pt-0 border-t border-border/40">
                  {canUpdate ? (
                    <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={() => openEdit(r)}>
                      Edit
                    </Button>
                  ) : null}
                  {r.status === "pending" && canUpdate ? (
                    <Button type="button" variant="premium" size="sm" className="h-8 text-xs gap-1" onClick={() => setMarkTarget(r)}>
                      <Wallet className="w-3 h-3" /> Mark paid
                    </Button>
                  ) : null}
                  {r.status === "pending" && canDelete ? (
                    <Button type="button" variant="ghost" size="sm" className="h-8 text-xs text-destructive" onClick={() => setDeleteTarget(r)}>
                      Delete
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.id}
                className={cn(
                  "glass-card rounded-xl border border-border/50 p-4 flex flex-col sm:flex-row sm:items-center gap-4",
                )}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div className="h-12 w-12 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0">
                    <Coins className="w-5 h-5 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{r.employeeName}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {r.periodLabel}
                      {r.employeeCode ? ` · ${r.employeeCode}` : ""}
                    </div>
                    <div className="mt-1">{statusPill(r.status)}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 text-sm sm:justify-end sm:text-right">
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Net</div>
                    <div className="font-bold tabular-nums text-emerald-700 dark:text-emerald-300">{formatMoney(r.netSalary)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-muted-foreground">Deductions</div>
                    <div className="font-semibold tabular-nums">{formatMoney(r.deductions)}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 sm:ml-auto">
                  {canUpdate ? (
                    <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => openEdit(r)}>
                      Edit
                    </Button>
                  ) : null}
                  {r.status === "pending" && canUpdate ? (
                    <Button type="button" variant="premium" size="sm" className="h-8 gap-1" onClick={() => setMarkTarget(r)}>
                      <Wallet className="w-3 h-3" /> Paid
                    </Button>
                  ) : null}
                  {r.status === "pending" && canDelete ? (
                    <Button type="button" variant="ghost" size="sm" className="h-8 text-destructive" onClick={() => setDeleteTarget(r)}>
                      Delete
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}

        <GridPaginationFooter
          totalCount={meta.total}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          page={page}
          totalPages={meta.lastPage}
          onPageChange={setPage}
        />

        <Dialog open={!!markTarget} onOpenChange={(o) => !o && setMarkTarget(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Mark as paid</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Label>Payment date</Label>
              <Input type="date" className="glass-input h-10" value={markDate} onChange={(e) => setMarkDate(e.target.value)} />
            </div>
            <DialogFooter>
              <Button variant="glass" onClick={() => setMarkTarget(null)} disabled={actionLoading}>
                Cancel
              </Button>
              <Button variant="premium" onClick={() => void confirmMarkPaid()} disabled={actionLoading}>
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ConfirmAlertDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          title="Delete this payment record?"
          description="This only removes the payment entry (pending). Payroll salary data is not deleted."
          mode="confirm"
          variant="danger"
          onConfirm={() => void confirmDelete()}
        />
      </div>
    </Layout>
  );
}
